import { usePostHog as usePostHogBase } from 'posthog-js/react';

import { config } from '~/config';
import { postLsegAnalyticsEvent } from '~/services/tracking/lseg-analytics-proxy/lseg-analytics-proxy-service';
import { type LsegPlatform, useApp } from '~/stores/use-app/useApp';
import { useAuthStore } from '~/stores/use-auth/store/authStore';
import { useFlags } from '~/stores/use-flags/useFlags';
import { useLanguageStore } from '~/stores/use-language-store/useLanguageStore';
import { useUser } from '~/stores/use-user/useUser';
import { createProductInsightEvent } from '~/utils/product-insight/product-insight-event';

import type {
  PosthogTrackingEventName,
  PosthogTrackingSource,
} from './constants';
import { PRODUCT_INSIGHT_EVENT_METADATA } from './product-insight-event-metadata';

export type CaptureUserProperties = {
  properties: {
    user_role?: string;
    user_firm_id?: string;
    user_created_at?: string;
    user_on_trial?: boolean;
    user_subscription?: string;
    user_subscription_next_payment?: string;
  };
  setOnce?: boolean;
};

interface CaptureTrackingEventParams {
  eventName: PosthogTrackingEventName;
  properties: Record<string, unknown> & {
    source: PosthogTrackingSource | null;
  };
}

const SYNTHETIC_USER_EMAIL_PREFIX = 'k6user+';
const PRODUCT_INSIGHT_PLATFORM: Record<LsegPlatform, string> = {
  ws: 'WORKSPACE',
  teams: 'MS TEAMS',
};

export const usePostHog = () => {
  const posthog = usePostHogBase();
  const shouldEnableTracking = (email: string) =>
    ['staging', 'prod'].includes(config.app.environment) &&
    !email.startsWith(SYNTHETIC_USER_EMAIL_PREFIX);

  const locale = useLanguageStore(state => state.languageSetting);
  const isProductInsightEnabled = useFlags(state =>
    state.getFeatureFlagState('product_insight_tracking')
  );

  const identifyUser = (userId: string, email: string, lsegId?: string) => {
    if (shouldEnableTracking(email)) {
      posthog.identify(userId, {
        userId,
        locale,
        lsegId,
      });
    }
  };

  const captureTrackingEvent = ({
    eventName,
    properties,
  }: CaptureTrackingEventParams) => {
    if (shouldEnableTracking(useUser.getState().user?.email ?? '')) {
      const { lsegPlatform } = useApp.getState();
      const enrichedProperties = {
        ...properties,
        ...(lsegPlatform && { platform: lsegPlatform }),
      };

      posthog.capture(eventName, enrichedProperties);

      if (!isProductInsightEnabled) {
        return;
      }

      const { lseg_id: lsegId } = useAuthStore.getState();

      if (!lsegId) {
        return;
      }

      const productInsightProperties = lsegPlatform
        ? {
            ...enrichedProperties,
            platform: PRODUCT_INSIGHT_PLATFORM[lsegPlatform],
          }
        : enrichedProperties;

      postLsegAnalyticsEvent(
        createProductInsightEvent({
          eventName,
          eventMetadata: PRODUCT_INSIGHT_EVENT_METADATA[eventName],
          eventUserId: lsegId,
          properties: productInsightProperties,
        })
      );
    }
  };

  const captureUserProperties = ({
    properties,
    setOnce = false,
  }: CaptureUserProperties) => {
    if (shouldEnableTracking(useUser.getState().user?.email ?? '')) {
      const payload = setOnce
        ? [undefined, properties]
        : [properties, undefined];
      posthog.setPersonProperties(...payload);
    }
  };

  return {
    identifyUser,
    captureTrackingEvent,
    captureUserProperties,
  };
};
